import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { SITE, MEDIA } from "../lib/site";
import { ShieldCheck, Crosshair, Plane } from "lucide-react";

export default function About() {
  const { t } = useLanguage();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#0A0A0A] py-24 sm:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-8 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="col-span-12 lg:col-span-7"
          >
            <div className="overline mb-6">{t.about.overline}</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white">
              {t.about.title}
            </h2>
            <div className="mt-3 font-mono-tech text-yellow-400 text-sm tracking-[0.2em] uppercase">
              {t.about.role}
            </div>

            <p className="mt-10 text-neutral-300 text-base sm:text-lg leading-relaxed max-w-2xl">
              {t.about.paragraph_1}
            </p>
            <p className="mt-6 text-neutral-400 text-base leading-relaxed max-w-2xl">
              {t.about.paragraph_2}
            </p>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-neutral-800">
              <Stat
                icon={<ShieldCheck className="h-4 w-4 text-yellow-400" />}
                label={t.about.stat_1_label}
                value={t.about.stat_1_value}
              />
              <Stat
                icon={<Crosshair className="h-4 w-4 text-yellow-400" />}
                label={t.about.stat_2_label}
                value={t.about.stat_2_value}
              />
              <Stat
                icon={<Plane className="h-4 w-4 text-yellow-400" />}
                label={t.about.stat_3_label}
                value={t.about.stat_3_value}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="relative border border-neutral-800 bg-[#0d0d0d] aspect-[4/5] overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center opacity-90"
                style={{ backgroundImage: `url(${MEDIA.blueprint})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 space-y-2">
                <div className="overline">// PROFIL TEHNIC</div>
                <div className="font-mono-tech text-xs text-neutral-300 leading-relaxed">
                  AUTH_NO &nbsp; <span className="text-yellow-400">{SITE.authNumber}</span><br />
                  SERIES &nbsp;&nbsp;&nbsp; <span className="text-yellow-400">{SITE.authSeries}</span><br />
                  CITY &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span className="text-neutral-200">{SITE.addressCity}</span>
                </div>
              </div>
              <div className="absolute top-4 right-4 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                <span className="h-1.5 w-1.5 bg-yellow-500" /> Blueprint · 01
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="bg-[#0A0A0A] p-5">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        {label}
      </span>
    </div>
    <div className="font-display font-semibold text-white text-lg">{value}</div>
  </div>
);
